import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Drama } from "lucide-react";
import { inIframe, openFullscreen, useEmbedAutoHeight } from "@/lib/embed";
import { homeFor } from "@/lib/roles";

const HERO = "https://customer-assets-agu9un31.emergentagent.net/job_iscritti-teatro/artifacts/v2llqmug_IMG_4139.png";
const LOGO = "https://www.areteteatro.com/wp-content/uploads/2020/03/logo-arete.png";

export default function Login() {
  const { user, loading, login, register } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [l, setL] = useState({ email: "", password: "" });
  const [r, setR] = useState({ name: "", email: "", password: "" });
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  const handleForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success(data?.message || "Se l'indirizzo è registrato, riceverai un'email.");
      setForgot(false);
      setForgotEmail("");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (!loading && user) navigate(homeFor(user.role), { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(l.email, l.password);
      toast.success("Bentornato!");
      navigate(homeFor(u.role));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(r.name, r.email, r.password);
      toast.success("Registrazione completata!");
      navigate("/dashboard");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEmbedAutoHeight();

  // Dentro l'iframe del sito: mostro una "porta d'ingresso" che apre l'area riservata
  // a tutto schermo (login/registrazione e dashboard scorrono senza problemi su mobile)
  if (inIframe) {
    return (
      <div className="min-h-[420px] flex items-center justify-center bg-background p-6 text-center">
        <div className="max-w-sm">
          <img src={LOGO} alt="Areté Teatro" className="h-20 w-20 rounded-full bg-white p-1 object-contain mx-auto mb-5" />
          <h1 className="text-2xl font-display text-foreground mb-2">Area riservata</h1>
          <p className="text-sm text-muted-foreground mb-6">Accedi o registrati per gestire iscrizione, lezioni di prova e pagamenti.</p>
          <Button data-testid="open-app-fullscreen" onClick={() => openFullscreen("/login")} className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
            Accedi all'area riservata →
          </Button>
          <p className="text-xs text-muted-foreground mt-4">Si aprirà a schermo intero per la migliore esperienza su telefono.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block grain bg-black">
        <img src={HERO} alt="Ensemble teatrale Areté" className="absolute inset-0 h-full w-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        <div className="absolute bottom-0 p-14">
          <div className="flex items-center gap-2 mb-6"><span className="h-px w-8 bg-primary" /><span className="text-xs tracking-[0.3em] uppercase text-white/70">Scuola di Teatro · Torino</span></div>
          <img src={LOGO} alt="Areté Teatro" className="h-32 w-32 rounded-full bg-white p-1.5 object-contain" />
          <p className="text-white/60 mt-6 max-w-sm text-lg">Prenota una lezione di prova, iscriviti all'anno scolastico e gestisci tutto in un unico posto.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 fade-up">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8 lg:hidden"><img src={LOGO} alt="Areté Teatro" className="h-12 w-12 rounded-full bg-white p-0.5 object-contain" /><span className="text-2xl font-display">Areté Teatro</span></div>
          <h2 className="text-3xl mb-1">Area Personale</h2>
          <p className="text-muted-foreground mb-4">Accedi o crea il tuo account.</p>
          <div className="mb-6 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3" data-testid="register-callout">
            <p className="text-sm text-foreground font-medium">Registrati per poter prenotare una lezione di prova gratuita e gestire la tua utenza!</p>
          </div>

          <Button onClick={googleLogin} data-testid="google-login-button" variant="outline" className="w-full mb-6 h-11 border-border hover:bg-secondary">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="mr-2 h-5 w-5 bg-white rounded-sm p-0.5" />
            Continua con Google
          </Button>

          <div className="flex items-center gap-3 mb-6"><div className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground uppercase tracking-widest">oppure</span><div className="h-px flex-1 bg-border" /></div>

          <Tabs defaultValue="login">
            <TabsList className="grid grid-cols-2 w-full mb-6">
              <TabsTrigger value="login" data-testid="tab-login">Accedi</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Registrati</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              {forgot ? (
                <form onSubmit={handleForgot} className="space-y-4" data-testid="forgot-form">
                  <p className="text-sm text-muted-foreground">Inserisci la tua email: ti invieremo una <strong>password temporanea</strong> per accedere.</p>
                  <div><Label>Email</Label><Input data-testid="forgot-email" type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="mt-1.5 bg-white" placeholder="tua@email.com" /></div>
                  <Button data-testid="forgot-submit" disabled={busy} className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-medium">{busy ? "Invio…" : "Invia password temporanea"}</Button>
                  <button type="button" data-testid="forgot-back" onClick={() => setForgot(false)} className="w-full text-sm text-muted-foreground hover:text-foreground">← Torna al login</button>
                </form>
              ) : (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div><Label>Email</Label><Input data-testid="login-email" type="email" required value={l.email} onChange={(e) => setL({ ...l, email: e.target.value })} className="mt-1.5 bg-white" placeholder="tua@email.com" /></div>
                  <div><Label>Password</Label><PasswordInput data-testid="login-password" required value={l.password} onChange={(e) => setL({ ...l, password: e.target.value })} className="mt-1.5 bg-white" placeholder="••••••••" /></div>
                  <Button data-testid="login-submit" disabled={busy} className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-medium">{busy ? "Attendere…" : "Accedi"}</Button>
                  <button type="button" data-testid="forgot-link" onClick={() => { setForgot(true); setForgotEmail(l.email); }} className="w-full text-sm text-primary hover:underline">Hai dimenticato la password?</button>
                </form>
              )}
            </TabsContent>
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div><Label>Nome e Cognome</Label><Input data-testid="register-name" required value={r.name} onChange={(e) => setR({ ...r, name: e.target.value })} className="mt-1.5 bg-white" placeholder="Mario Rossi" /></div>
                <div><Label>Email</Label><Input data-testid="register-email" type="email" required value={r.email} onChange={(e) => setR({ ...r, email: e.target.value })} className="mt-1.5 bg-white" placeholder="tua@email.com" /></div>
                <div><Label>Password</Label><PasswordInput data-testid="register-password" required minLength={6} value={r.password} onChange={(e) => setR({ ...r, password: e.target.value })} className="mt-1.5 bg-white" placeholder="Almeno 6 caratteri" /></div>
                <Button data-testid="register-submit" disabled={busy} className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-medium">{busy ? "Attendere…" : "Crea account"}</Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
